package fr.frux.feature.premium

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.CloudDownload
import androidx.compose.material.icons.outlined.Notifications
import androidx.compose.material.icons.outlined.VolumeOff
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import fr.frux.core.common.entitlements.PremiumSource
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.premium.resources.Res
import fr.frux.feature.premium.resources.premium_benefit_alerts_text
import fr.frux.feature.premium.resources.premium_benefit_alerts_title
import fr.frux.feature.premium.resources.premium_benefit_no_ads_text
import fr.frux.feature.premium.resources.premium_benefit_no_ads_title
import fr.frux.feature.premium.resources.premium_benefit_offline_text
import fr.frux.feature.premium.resources.premium_benefit_offline_title
import fr.frux.feature.premium.resources.premium_benefit_soon
import fr.frux.feature.premium.resources.premium_benefits_title
import fr.frux.feature.premium.resources.premium_donation_notice
import fr.frux.feature.premium.resources.premium_free_notice
import fr.frux.feature.premium.resources.premium_intro
import fr.frux.feature.premium.resources.premium_manage_subscription
import fr.frux.feature.premium.resources.premium_refresh_error
import fr.frux.feature.premium.resources.premium_retry
import fr.frux.feature.premium.resources.premium_status_active
import fr.frux.feature.premium.resources.premium_status_free
import fr.frux.feature.premium.resources.premium_status_gifted
import fr.frux.feature.premium.resources.premium_status_loading
import fr.frux.feature.premium.resources.premium_status_no_end
import fr.frux.feature.premium.resources.premium_status_unknown
import fr.frux.feature.premium.resources.premium_status_until
import fr.frux.feature.premium.resources.premium_subscribe
import fr.frux.feature.premium.resources.premium_title
import fr.frux.feature.premium.resources.premium_unavailable
import fr.frux.shared.domain.model.PremiumStatus
import org.jetbrains.compose.resources.stringResource

/**
 * Écran Frux+ (#456, ADR 0016) : ce que l'abonnement apporte, l'état courant et son échéance. Le
 * bouton d'achat n'existe que si une plateforme fournit un [PurchaseLauncher] (#457). Le don de la
 * cagnotte est dit distinct : il n'ouvre rien.
 */
@Composable
fun PremiumScreen(
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PremiumViewModel = rememberPremiumViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }

    SnackbarMessageEffect(
        trigger = state.purchaseError,
        message = state.purchaseError?.userMessage(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumePurchaseError() }

    Scaffold(
        modifier = modifier.fillMaxSize(),
        topBar = { FruxTopBar(title = stringResource(Res.string.premium_title), onBack = onBack) },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        Column(
            modifier =
                Modifier
                    .padding(paddingValues)
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        ) {
            Text(text = stringResource(Res.string.premium_intro), style = MaterialTheme.typography.bodyLarge)
            StatusBlock(state = state, onRetry = viewModel::refresh)
            PurchaseBlock(state = state, onPurchase = viewModel::purchase)
            Text(
                text = stringResource(Res.string.premium_benefits_title),
                style = MaterialTheme.typography.titleMedium,
            )
            Benefit(
                icon = Icons.Outlined.Notifications,
                title = stringResource(Res.string.premium_benefit_alerts_title),
                text = stringResource(Res.string.premium_benefit_alerts_text),
            )
            Benefit(
                icon = Icons.Outlined.CloudDownload,
                title = stringResource(Res.string.premium_benefit_offline_title),
                text = stringResource(Res.string.premium_benefit_offline_text),
                badge = stringResource(Res.string.premium_benefit_soon),
            )
            Benefit(
                icon = Icons.Outlined.VolumeOff,
                title = stringResource(Res.string.premium_benefit_no_ads_title),
                text = stringResource(Res.string.premium_benefit_no_ads_text),
            )
            Text(
                text = stringResource(Res.string.premium_free_notice),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = stringResource(Res.string.premium_donation_notice),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun StatusBlock(
    state: PremiumUiState,
    onRetry: () -> Unit,
) {
    when (val status = state.status) {
        PremiumStatus.Unknown -> {
            if (state.isLoading) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                    Text(
                        text = stringResource(Res.string.premium_status_loading),
                        style = MaterialTheme.typography.bodyLarge,
                    )
                }
            } else {
                Text(
                    text = stringResource(Res.string.premium_status_unknown),
                    style = MaterialTheme.typography.bodyLarge,
                )
                FruxButton(text = stringResource(Res.string.premium_retry), onClick = onRetry)
            }
        }

        PremiumStatus.Free -> {
            Text(text = stringResource(Res.string.premium_status_free), style = MaterialTheme.typography.titleMedium)
            RefreshErrorNotice(state = state, onRetry = onRetry)
        }

        is PremiumStatus.Active -> {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
            ) {
                Icon(
                    imageVector = Icons.Outlined.CheckCircle,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp),
                )
                Column {
                    Text(
                        text = stringResource(Res.string.premium_status_active),
                        style = MaterialTheme.typography.titleMedium,
                    )
                    val expiresAt = status.expiresAt
                    Text(
                        text =
                            if (expiresAt != null) {
                                stringResource(Res.string.premium_status_until, formatPremiumDate(expiresAt))
                            } else {
                                stringResource(Res.string.premium_status_no_end)
                            },
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    if (status.source == PremiumSource.MANUAL) {
                        Text(
                            text = stringResource(Res.string.premium_status_gifted),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
            }
            if (status.source == PremiumSource.GOOGLE_PLAY) ManageSubscriptionLink()
            RefreshErrorNotice(state = state, onRetry = onRetry)
        }
    }
}

/**
 * Résiliation et moyen de paiement se gèrent chez Google, jamais dans Frux (#457) : le droit suit
 * ensuite par les notifications de Play au serveur.
 */
@Composable
private fun ManageSubscriptionLink() {
    val uriHandler = LocalUriHandler.current
    FruxButton(
        text = stringResource(Res.string.premium_manage_subscription),
        onClick = { uriHandler.openUri(PLAY_SUBSCRIPTIONS_URL) },
        style = FruxButtonStyle.Outline,
    )
}

private const val PLAY_SUBSCRIPTIONS_URL =
    "https://play.google.com/store/account/subscriptions?sku=frux_plus&package=fr.frux"

/** Échec de relecture non bloquant : le dernier statut connu reste affiché au-dessus. */
@Composable
private fun RefreshErrorNotice(
    state: PremiumUiState,
    onRetry: () -> Unit,
) {
    if (state.refreshError == null) return
    Text(
        text = stringResource(Res.string.premium_refresh_error),
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.error,
    )
    FruxButton(text = stringResource(Res.string.premium_retry), onClick = onRetry)
}

@Composable
private fun PurchaseBlock(
    state: PremiumUiState,
    onPurchase: () -> Unit,
) {
    when {
        !state.purchaseAvailable -> {
            // Un compte déjà abonné n'a pas besoin de savoir que l'achat n'est pas ouvert.
            if (state.status !is PremiumStatus.Active) {
                Text(
                    text = stringResource(Res.string.premium_unavailable),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }

        state.status is PremiumStatus.Free -> {
            FruxButton(
                text = stringResource(Res.string.premium_subscribe),
                onClick = onPurchase,
                enabled = state.canPurchase,
                loading = state.isPurchasing,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun Benefit(
    icon: ImageVector,
    title: String,
    text: String,
    badge: String? = null,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.md)) {
        Icon(imageVector = icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
            ) {
                Text(text = title, style = MaterialTheme.typography.titleSmall)
                if (badge != null) {
                    Text(
                        text = badge,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.tertiary,
                    )
                }
            }
            Text(text = text, style = MaterialTheme.typography.bodyMedium)
        }
    }
}
